import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, 
  UserPlus, 
  Trash2, 
  Mail,
  User,
  Lock,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminManagePage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New admin form
  const [showForm, setShowForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/admins`);
      setAdmins(response.data.admins);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast.error('Admins konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdmin.email || !newAdmin.password || !newAdmin.name) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    
    if (newAdmin.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    setCreating(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/admins`, newAdmin);
      toast.success(response.data.message);
      setNewAdmin({ name: '', email: '', password: '' });
      setShowForm(false);
      fetchAdmins();
    } catch (error) {
      const message = error.response?.data?.detail || 'Admin konnte nicht erstellt werden';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveAdmin = async (adminId, adminName) => {
    if (!window.confirm(`Admin-Berechtigung von ${adminName} entfernen?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/admin/admins/${adminId}`);
      toast.success('Admin-Berechtigung entfernt');
      fetchAdmins();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Entfernen';
      toast.error(message);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Layout>
      <div className=" animate-slide-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-foreground uppercase tracking-tight">
              Admin Verwaltung
            </h1>
            <p className="text-muted-foreground mt-1">
              Administratoren hinzufügen und verwalten
            </p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-primary hover:bg-red-700 text-foreground uppercase tracking-widest font-bold text-sm rounded-none btn-glow"
            data-testid="add-admin-btn"
          >
            <UserPlus className="mr-2" size={18} />
            Admin hinzufügen
          </Button>
        </div>

        {/* Create Admin Form */}
        {showForm && (
          <div className="bg-card border border-border p-6 mb-8 animate-fade-in">
            <h2 className="font-['Oswald'] text-xl font-bold text-foreground uppercase tracking-tight mb-6">
              Neuen Admin erstellen
            </h2>
            
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-name" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Name
                  </Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="admin-name"
                      type="text"
                      value={newAdmin.name}
                      onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                      placeholder="Max Mustermann"
                      className="pl-12 bg-secondary/50 border-border rounded-none"
                      required
                      data-testid="new-admin-name"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin-email" className="text-xs uppercase tracking-wider text-muted-foreground">
                    E-Mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="admin-email"
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                      placeholder="admin@shrt.ch"
                      className="pl-12 bg-secondary/50 border-border rounded-none"
                      required
                      data-testid="new-admin-email"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin-password" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Passwort
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="admin-password"
                      type={showPassword ? "text" : "password"}
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                      placeholder="Min. 8 Zeichen"
                      className="pl-12 pr-12 bg-secondary/50 border-border rounded-none"
                      required
                      minLength={8}
                      data-testid="new-admin-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={creating}
                  className="bg-primary hover:bg-red-700 text-foreground uppercase tracking-widest font-bold text-sm rounded-none btn-glow"
                  data-testid="create-admin-submit"
                >
                  {creating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Shield className="mr-2" size={18} />
                      Admin erstellen
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowForm(false)}
                  className="text-muted-foreground hover:text-foreground rounded-none"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Admins List */}
        <div className="bg-card border border-border overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="font-['Oswald'] text-xl font-bold text-foreground uppercase tracking-tight flex items-center gap-3">
              <Shield size={24} />
              Aktive Administratoren ({admins.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-secondary/30">
                  <div className="w-12 h-12 skeleton" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 skeleton" />
                    <div className="h-3 w-48 skeleton" />
                  </div>
                </div>
              ))}
            </div>
          ) : admins.length > 0 ? (
            <div className="divide-y divide-border">
              {admins.map((admin) => (
                <div 
                  key={admin.id} 
                  className="flex items-center justify-between p-6 hover:bg-muted/50 transition-colors"
                  data-testid={`admin-row-${admin.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/20 flex items-center justify-center">
                      <span className="font-['Oswald'] text-lg font-bold text-primary">
                        {getInitials(admin.name)}
                      </span>
                    </div>
                    <div>
                      <p className="text-foreground font-medium">{admin.name}</p>
                      <p className="text-muted-foreground text-sm">{admin.email}</p>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAdmin(admin.id, admin.name)}
                    className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                    data-testid={`remove-admin-${admin.id}`}
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-muted-foreground">
              Keine Administratoren vorhanden
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
